import { WefatComponentRuntimeError } from './component-error.js';
const TOUCH_NUMBER_FIELDS = Object.freeze([
    'identifier',
    'clientX',
    'clientY',
    'pageX',
    'pageY',
    'screenX',
    'screenY',
    'force',
]);
function adapterError(message, remediation) {
    return new WefatComponentRuntimeError('WF5004', message, remediation);
}
function isRecord(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}
function numberValue(value, fallback = 0) {
    return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}
function targetSnapshot(value) {
    return Object.freeze({
        id: isRecord(value) && typeof value.id === 'string' ? value.id : '',
    });
}
function touchSnapshot(value) {
    if (!isRecord(value)) {
        throw adapterError('Browser touch data must contain touch-point objects', 'Pass the native TouchList or an array of touch-like records to the event adapter.');
    }
    const touch = {};
    for (const field of TOUCH_NUMBER_FIELDS) {
        const candidate = value[field];
        if (typeof candidate === 'number' && Number.isFinite(candidate)) {
            touch[field] = candidate;
        }
    }
    return Object.freeze(touch);
}
function touchListSnapshot(value, label) {
    if (value === undefined || value === null) {
        return Object.freeze([]);
    }
    if (Array.isArray(value)) {
        return Object.freeze(value.map(touchSnapshot));
    }
    if (!isRecord(value)) {
        throw adapterError(`${label} must be an array or array-like TouchList`, 'Pass native touch data through the components-web event adapter.');
    }
    const length = value.length;
    if (!Number.isInteger(length) || typeof length !== 'number' || length < 0 || length > 100) {
        throw adapterError(`${label} has an invalid TouchList length`, 'Normalize the touch list to at most 100 touch points before dispatch.');
    }
    const touches = [];
    for (let index = 0; index < length; index += 1) {
        touches.push(touchSnapshot(value[String(index)]));
    }
    return Object.freeze(touches);
}
function tapDetail(event) {
    return Object.freeze({
        x: numberValue(event.clientX),
        y: numberValue(event.clientY),
    });
}
function nativeEventInput(eventName, input) {
    if (!isRecord(input)) {
        throw adapterError(`Native ${eventName} input must be an event-like object`, 'Pass the listener argument received from Vue to adaptWefatEvent.');
    }
    const stopPropagation = input.stopPropagation;
    return Object.freeze({
        timeStamp: numberValue(input.timeStamp),
        target: targetSnapshot(input.target),
        currentTarget: targetSnapshot(input.currentTarget),
        detail: eventName === 'tap'
            ? tapDetail(input)
            : input.detail === undefined ? Object.freeze({}) : input.detail,
        touches: touchListSnapshot(input.touches, 'touches'),
        changedTouches: touchListSnapshot(input.changedTouches, 'changedTouches'),
        mark: isRecord(input.mark) ? input.mark : Object.freeze({}),
        ...(typeof stopPropagation === 'function'
            ? { stopPropagation: () => stopPropagation.call(input) }
            : {}),
    });
}
function detailEventInput(input) {
    return Object.freeze({
        timeStamp: 0,
        target: null,
        currentTarget: null,
        detail: input,
        touches: Object.freeze([]),
        changedTouches: Object.freeze([]),
        mark: Object.freeze({}),
        stopPropagation() { },
    });
}
export function adaptWefatEvent(kind, eventName, input) {
    if (kind === 'detail') {
        return detailEventInput(input);
    }
    if (kind === 'native') {
        return nativeEventInput(eventName, input);
    }
    throw adapterError(`Unsupported Wefat event input kind: ${String(kind)}`, 'Use native for DOM listeners or detail for normalized component emissions.');
}
//# sourceMappingURL=mini-event-adapter.js.map